import { Injectable } from '@angular/core';
import { Observable, from, of, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { resource } from '@util/resource/resource';
import { toast } from '@web/util/toast';
import { tr } from '@util/tr';

/**
 * Stripe Terminal SDK types and interfaces
 */
declare global {
  interface Window {
    StripeTerminal?: any;
  }
}

export interface StripeTerminalOptions {
  onFetchConnectionToken: () => Promise<string>;
  onUnexpectedReaderDisconnect?: () => void;
  onConnectionStatusChange?: (status: ConnectionStatus) => void;
  onPaymentStatusChange?: (status: PaymentStatus) => void;
  readerBehavior?: {
    allowCustomerCancel?: boolean;
  };
}

export type ConnectionStatus = 'connecting' | 'connected' | 'not_connected';
export type PaymentStatus = 'not_ready' | 'ready' | 'waiting_for_input' | 'processing';

export interface Reader {
  id: string;
  object: string;
  device_type: string;
  ip_address?: string;
  label?: string;
  location?: string;
  serial_number?: string;
  status?: string;
  [key: string]: any;
}

export interface DiscoverReadersOptions {
  simulated?: boolean;
  location?: string;
}

export interface DiscoverReadersResponse {
  discoveredReaders?: Reader[];
  error?: StripeTerminalError;
}

export interface ConnectReaderOptions {
  fail_if_in_use?: boolean;
}

export interface ConnectReaderResponse {
  reader?: Reader;
  error?: StripeTerminalError;
}

export interface CollectPaymentMethodOptions {
  config_override?: {
    skip_tipping?: boolean;
    tipping?: {
      eligible_amount?: number;
    };
    update_payment_intent?: boolean;
    payment_intent_id?: string;
    enable_customer_cancellation?: boolean;
    allow_redisplay?: string;
    moto?: boolean;
  };
}

export interface CollectPaymentMethodResponse {
  paymentIntent?: any;
  error?: StripeTerminalError;
}

export interface ProcessPaymentOptions {
  config_override?: {
    return_url?: string;
  };
}

export interface ProcessPaymentResponse {
  paymentIntent?: any;
  error?: StripeTerminalError;
}

export interface CollectSetupIntentPaymentMethodConfig {
  enable_customer_cancellation?: boolean;
  moto?: boolean;
}

export interface CollectSetupIntentPaymentMethodResponse {
  setupIntent?: any;
  error?: StripeTerminalError;
}

export interface ConfirmSetupIntentResponse {
  setupIntent?: any;
  error?: StripeTerminalError;
}

export interface ReadReusableCardResponse {
  payment_method?: any;
  error?: StripeTerminalError;
}

export interface SetReaderDisplayCart {
  line_items: Array<{
    description: string;
    amount: number;
    quantity: number;
  }>;
  tax: number;
  total: number;
  currency: string;
}

export interface SetReaderDisplayInfo {
  type: 'cart';
  cart: SetReaderDisplayCart;
}

export interface SetReaderDisplayResponse {
  error?: StripeTerminalError;
}

export interface SimulatorConfiguration {
  testCardNumber?: string;
  testPaymentMethod?: string;
  tipAmount?: number | null;
  collectInputsResult?: {
    resultType: 'succeeded' | 'timeout';
    skipBehavior?: 'none' | 'all';
  };
  paymentMethodType?: 'card_present' | 'interac_present';
}

export interface CollectRefundPaymentMethodOptions {
  refund_application_fee?: boolean;
  reverse_transfer?: boolean;
}

export interface CollectRefundPaymentMethodConfig {
  enable_customer_cancellation?: boolean;
}

export interface CollectRefundPaymentMethodResponse {
  error?: StripeTerminalError;
}

export interface ProcessRefundResponse {
  refund?: any;
  error?: StripeTerminalError;
}

export interface CollectInputsParameters {
  [key: string]: any;
}

export interface CollectInputsResponse {
  [key: string]: any;
  error?: StripeTerminalError;
}

export interface PrintResponse {
  error?: StripeTerminalError;
}

export interface StripeTerminalError {
  code: string;
  message: string;
  payment_intent?: any;
}

@Injectable({
  providedIn: 'root'
})
export class StripeTerminalService {
  // Version control flag for v2 payment flow
  public static USE_v2: boolean = true;
  
  private terminal: any = null;
  private isInitialized = false;
  
  // Observable state
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>('not_connected');
  private readerSubject = new BehaviorSubject<Reader | null>(null);
  
  // Public observables
  public connectionStatus$: Observable<ConnectionStatus> = this.connectionStatusSubject.asObservable();
  public reader$: Observable<Reader | null> = this.readerSubject.asObservable();
  private scriptLoaded = false;

  /**
   * Loads the Stripe Terminal SDK script
   */
  private loadScript(): Promise<void> {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (window.StripeTerminal) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/terminal/v1/';
      script.async = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Stripe Terminal SDK'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Creates an instance of StripeTerminal with the given options
   */
  async create(options: StripeTerminalOptions): Promise<any> {
    try {
      await this.loadScript();

      if (!window.StripeTerminal) {
        throw new Error('Stripe Terminal SDK is not available');
      }

      this.terminal = window.StripeTerminal.create(options);
      this.isInitialized = true;
      return this.terminal;
    } catch (error) {
      console.error('Error creating Stripe Terminal instance:', error);
      throw error;
    }
  }

  /**
   * Initializes Stripe Terminal SDK with wineryId
   * This method handles the full initialization including connection token fetching and auto-connect
   */
  async initialize(
    wineryId: string,
    options?: {
      autoConnect?: boolean;
      simulated?: boolean;
      wineryChannelId?: string;
      onPaymentStatusChange?: (status: PaymentStatus) => void;
    }
  ): Promise<void> {
    try {
      // Fetch connection token from backend with wineryId
      const fetchConnectionToken = async (): Promise<string> => {
        try {
          // Call backend endpoint to fetch Stripe Terminal connection token
          // The backend proxies to ws-payment service which creates a connection token using Stripe API
          const response = await resource('crm://wspayments/stripe-terminal/connection-token')
            .post<{ secret: string }>({ wineryId })
            .toPromise();
          
          if (!response || !response.secret) {
            throw new Error('Invalid connection token response from backend');
          }
          
          return response.secret;
        } catch (error) {
          console.error('Error fetching Stripe Terminal connection token:', error);
          // If the endpoint doesn't exist yet, provide a helpful error message
          const err = error as any;
          if (err.status === 404 || err.status === 0) {
            throw new Error('Stripe Terminal connection token endpoint not found. Please implement the backend endpoint at /api/wspayments/stripe-terminal/connection-token');
          }
          throw new Error(err.message || 'Failed to fetch connection token');
        }
      };

      await this.create({
        onFetchConnectionToken: fetchConnectionToken,
        onUnexpectedReaderDisconnect: () => {
          console.warn('Stripe Terminal reader disconnected unexpectedly');
          this.connectionStatusSubject.next('not_connected');
          this.readerSubject.next(null);
          toast(tr('pos.terminal.readerDisconnected'));
        },
        onConnectionStatusChange: (status) => {
          console.log('Stripe Terminal connection status:', status);
          this.connectionStatusSubject.next(status);
        },
        onPaymentStatusChange: options?.onPaymentStatusChange,
        readerBehavior: {
          allowCustomerCancel: true
        }
      });

      console.log('Stripe Terminal initialized successfully');
      
      // Auto-connect if requested: use point-of-sale and terminals for the channel (wineryChannelId required)
      if (options?.autoConnect !== false && options?.wineryChannelId) {
        await this.autoConnectReaderForChannel(wineryId, options.wineryChannelId);
      }
    } catch (error) {
      console.error('Failed to initialize Stripe Terminal:', error);
      this.connectionStatusSubject.next('not_connected');
      throw error;
    }
  }

  /**
   * Fetches point-of-sale and terminal(s) for the winery channel, discovers readers for that location only,
   * filters to terminals linked to the point of sale, connects to the first matching reader, returns all matching readers.
   */
  async autoConnectReaderForChannel(wineryId: string, wineryChannelId: string): Promise<Reader[]> {
    if (!this.isTerminalInitialized()) {
      return [];
    }

    try {
      this.connectionStatusSubject.next('connecting');

      // 1) Retrieve point of sale and terminal(s) from winery channel (single source for locationId)
      const posTerminalInfo = await resource('crm://winery/wineryId/wineryChannel/id/pos-terminal')
        .params({ wineryId, id: wineryChannelId })
        .get<{ wineryChannelId: string; pointOfSaleWineSuiteId: string; terminalId: string; locationId: string }>()
        .toPromise()
        .catch((err) => {
          console.warn('Could not load POS terminal info for channel:', err);
          return null;
        });

      if (!posTerminalInfo?.locationId || !posTerminalInfo?.terminalId) {
        this.connectionStatusSubject.next('not_connected');
        return [];
      }

      // 2) Single discovery with locationId
      const discoverResult = await this.discoverReaders({
        simulated: false,
        location: posTerminalInfo.locationId
      }).toPromise();

      if (!discoverResult?.discoveredReaders?.length) {
        this.connectionStatusSubject.next('not_connected');
        return [];
      }

      // Filter to readers that match the terminal(s) linked to the point of sale
      const terminalIds = [posTerminalInfo.terminalId];
      const matchingReaders = discoverResult.discoveredReaders.filter((r) => terminalIds.includes(r.id));

      if (matchingReaders.length === 0) {
        this.connectionStatusSubject.next('not_connected');
        return [];
      }

      // 3) Auto-connect to first; show all in actions
      const firstReader = matchingReaders[0];
      const connectResult = await this.connectReader(firstReader).toPromise();

      if (connectResult?.reader) {
        this.readerSubject.next(connectResult.reader);
        this.connectionStatusSubject.next('connected');
        toast(tr('pos.terminal.readerConnected'));
      } else if (connectResult?.error) {
        console.error('Failed to connect reader:', connectResult.error);
        this.connectionStatusSubject.next('not_connected');
        toast(tr('pos.terminal.failedToConnectTerminal') || connectResult.error.message || 'Failed to connect reader');
      } else {
        this.connectionStatusSubject.next('not_connected');
        toast(tr('pos.terminal.failedToConnectTerminal') || 'Failed to connect reader');
      }
      return matchingReaders;
    } catch (error) {
      console.error('Error auto-connecting reader for channel:', error);
      this.connectionStatusSubject.next('not_connected');
      return [];
    }
  }

  /**
   * Begins discovering readers. Requires locationId when not simulated (never discover without location).
   */
  discoverReaders(options?: DiscoverReadersOptions): Observable<DiscoverReadersResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }
    const opts = options || {};
    if (opts.simulated !== true && !opts.location) {
      return of({ error: { code: 'location_required', message: 'locationId is required to discover readers' } });
    }

    return from(this.terminal.discoverReaders(opts) as Promise<DiscoverReadersResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Attempts to connect to the given reader with the given options.
   * Emits 'connecting' once so the component does not need to set status (avoids double status update).
   */
  connectReader(reader: Reader, options?: ConnectReaderOptions): Observable<ConnectReaderResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }
    this.connectionStatusSubject.next('connecting');

    return from(this.terminal.connectReader(reader, options || {}) as Promise<ConnectReaderResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Disconnects from the connected reader
   */
  disconnectReader(): Observable<void> {
    if (!this.terminal) {
      return of(undefined as void);
    }

    return from(this.terminal.disconnectReader() as Promise<void>).pipe(
      catchError((error: any) => {
        console.error('Error disconnecting reader:', error);
        return of(undefined as void);
      })
    );
  }

  /**
   * Gets the current connection status (synchronous)
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatusSubject.value;
  }

  /**
   * Gets the current reader (synchronous)
   */
  getReader(): Reader | null {
    return this.readerSubject.value;
  }

  /**
   * Returns the reader's payment status
   */
  getPaymentStatus(): PaymentStatus {
    if (!this.terminal) {
      return 'not_ready';
    }

    try {
      return this.terminal.getPaymentStatus();
    } catch (error) {
      console.error('Error getting payment status:', error);
      return 'not_ready';
    }
  }

  /**
   * Clears the current ConnectionToken and any other cached credentials
   */
  clearCachedCredentials(): Observable<void> {
    if (!this.terminal) {
      return of(undefined as void);
    }

    return from(this.terminal.clearCachedCredentials() as Promise<void>).pipe(
      catchError((error: any) => {
        console.error('Error clearing cached credentials:', error);
        return of(undefined as void);
      })
    );
  }

  /**
   * Begins collecting a payment method for a PaymentIntent
   */
  collectPaymentMethod(
    request: string,
    options?: CollectPaymentMethodOptions
  ): Observable<CollectPaymentMethodResponse> {
    console.log('=== collectPaymentMethod CALLED ===');
    console.log('ClientSecret (request):', request);
    console.log('Options:', JSON.stringify(options, null, 2));
    
    if (!this.terminal) {
      console.error('collectPaymentMethod: Terminal instance is NULL');
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    console.log('collectPaymentMethod: Terminal instance exists, calling terminal.collectPaymentMethod()...');
    console.log('This should trigger the terminal to display the payment amount');
    
    const terminalPromise = this.terminal.collectPaymentMethod(request, options) as Promise<CollectPaymentMethodResponse>;
    
    return from(terminalPromise).pipe(
      catchError((error: any) => {
        console.error('=== collectPaymentMethod EXCEPTION ===');
        console.error('Error type:', typeof error);
        console.error('Error code:', error?.code);
        console.error('Error message:', error?.message);
        console.error('Error name:', error?.name);
        console.error('Error stack:', error?.stack);
        console.error('Full error object:', error);
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Cancels an outstanding collectPaymentMethod command
   */
  cancelCollectPaymentMethod(): Observable<{ error?: StripeTerminalError }> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.cancelCollectPaymentMethod() as Promise<{ error?: StripeTerminalError }>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Processes a payment after a payment method has been collected
   */
  processPayment(
    paymentIntent: any,
    options?: ProcessPaymentOptions
  ): Observable<ProcessPaymentResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.processPayment(paymentIntent, options) as Promise<ProcessPaymentResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Cancels an outstanding processPayment command
   */
  cancelProcessPayment(): Observable<{ error?: StripeTerminalError }> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.cancelProcessPayment() as Promise<{ error?: StripeTerminalError }>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Begins collecting a payment method for online reuse for a SetupIntent
   */
  collectSetupIntentPaymentMethod(
    clientSecret: string,
    allowRedisplay: string,
    config?: CollectSetupIntentPaymentMethodConfig
  ): Observable<CollectSetupIntentPaymentMethodResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.collectSetupIntentPaymentMethod(clientSecret, allowRedisplay, config) as Promise<CollectSetupIntentPaymentMethodResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Cancels an outstanding collectSetupIntentPaymentMethod command
   */
  cancelCollectSetupIntentPaymentMethod(): Observable<{ error?: StripeTerminalError }> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.cancelCollectSetupIntentPaymentMethod() as Promise<{ error?: StripeTerminalError }>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Confirms a SetupIntent after a payment method has been collected
   * @deprecated Use confirmSetupIntentOnBackend instead for subscriptions to get reusable 'card' payment method
   * This method confirms via Terminal SDK which creates 'card_present' payment method (not suitable for subscriptions)
   */
  confirmSetupIntent(setupIntent: any): Observable<ConfirmSetupIntentResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.confirmSetupIntent(setupIntent) as Promise<ConfirmSetupIntentResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Confirms SetupIntent on backend (server-side) to create reusable payment method.
   * This ensures we get a "card" type payment method, not "card_present".
   * 
   * Critical: SetupIntent confirmation MUST happen server-side (backend) using Stripe API,
   * NOT via Terminal SDK, to create a reusable 'card' payment method suitable for subscriptions.
   * 
   * @param wineryId The winery ID
   * @param setupIntentId The SetupIntent ID to confirm
   * @returns Observable with confirmation result containing reusable payment method ID
   */
  confirmSetupIntentOnBackend(
    wineryId: string,
    setupIntentId: string
  ): Observable<{
    success: boolean;
    setupIntent?: any;
    paymentMethodId?: string;
    error?: string;
  }> {
    return resource('crm://wspayments/stripe-terminal/setup-intent/id/confirm')
      .params({ setupIntentId: setupIntentId })
      .post<{
        success: boolean;
        setupIntent: any;
        paymentMethodId: string;
      }>({ wineryId })
      .pipe(
        map(response => ({
          success: true,
          setupIntent: response.setupIntent,
          paymentMethodId: response.paymentMethodId
        })),
        catchError(error => {
          console.error('Failed to confirm SetupIntent on backend:', error);
          const errorMessage = error?.error?.message || error?.message || 'Failed to confirm SetupIntent on backend';
          return of({
            success: false,
            error: errorMessage
          });
        })
      );
  }

  /**
   * Cancels an outstanding confirmSetupIntent command
   */
  cancelConfirmSetupIntent(): Observable<{ error?: StripeTerminalError }> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.cancelConfirmSetupIntent() as Promise<{ error?: StripeTerminalError }>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Reads a card for online reuse
   */
  readReusableCard(): Observable<ReadReusableCardResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.readReusableCard() as Promise<ReadReusableCardResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Cancels an outstanding readReusableCard command
   */
  cancelReadReusableCard(): Observable<{ error?: StripeTerminalError }> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.cancelReadReusableCard() as Promise<{ error?: StripeTerminalError }>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Enhances cart data with cancellation instructions if enabled
   */
  private enhanceCartWithCancellationInfo(
    cartData: SetReaderDisplayCart,
    enableCustomerCancellation?: boolean
  ): SetReaderDisplayCart {
    if (!enableCustomerCancellation) {
      return cartData;
    }

    // Add cancellation instruction as a zero-amount line item
    // This will be shown on the reader display to inform customers they can cancel
    const enhancedCart: SetReaderDisplayCart = {
      ...cartData,
      line_items: [
        ...cartData.line_items,
        {
          description: 'Premere CANC per annullare',
          amount: 0,
          quantity: 1
        }
      ]
    };

    return enhancedCart;
  }

  /**
   * Clears the reader display - call this when starting a new order
   */
  clearDisplayForNewOrder(): Observable<SetReaderDisplayResponse> {
    return this.clearReaderDisplay();
  }

  /**
   * Cleanup method - call this on component/service destroy
   */
  cleanup(): Observable<SetReaderDisplayResponse> {
    return this.clearReaderDisplay();
  }

  /**
   * Updates the reader display with cart details
   */
  setReaderDisplay(displayInfo: SetReaderDisplayInfo): Observable<SetReaderDisplayResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.setReaderDisplay(displayInfo) as Promise<SetReaderDisplayResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Clears the reader display and resets it to the splash screen
   */
  clearReaderDisplay(): Observable<SetReaderDisplayResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.clearReaderDisplay() as Promise<SetReaderDisplayResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Sets the configuration object for the simulated card reader
   */
  setSimulatorConfiguration(configuration: SimulatorConfiguration): Observable<void> {
    if (!this.terminal) {
      return of(undefined as void);
    }

    return from(this.terminal.setSimulatorConfiguration(configuration) as Promise<void>).pipe(
      catchError((error: any) => {
        console.error('Error setting simulator configuration:', error);
        return of(undefined as void);
      })
    );
  }

  /**
   * Returns the currently active configuration object
   */
  getSimulatorConfiguration(): SimulatorConfiguration | null {
    if (!this.terminal) {
      return null;
    }

    try {
      return this.terminal.getSimulatorConfiguration();
    } catch (error) {
      console.error('Error getting simulator configuration:', error);
      return null;
    }
  }

  /**
   * Begins collecting a payment method to be refunded
   */
  collectRefundPaymentMethod(
    charge_id: string,
    amount: number,
    currency: string,
    options?: CollectRefundPaymentMethodOptions,
    config?: CollectRefundPaymentMethodConfig
  ): Observable<CollectRefundPaymentMethodResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.collectRefundPaymentMethod(charge_id, amount, currency, options, config) as Promise<CollectRefundPaymentMethodResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Cancels an outstanding collectRefundPaymentMethod command
   */
  cancelCollectRefundPaymentMethod(): Observable<{ error?: StripeTerminalError }> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.cancelCollectRefundPaymentMethod() as Promise<{ error?: StripeTerminalError }>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Processes an in-progress refund
   */
  processRefund(): Observable<ProcessRefundResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.processRefund() as Promise<ProcessRefundResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Cancels an outstanding processRefund command
   */
  cancelProcessRefund(): Observable<{ error?: StripeTerminalError }> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.cancelProcessRefund() as Promise<{ error?: StripeTerminalError }>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Start displaying forms and collecting information from customers
   */
  collectInputs(parameters: CollectInputsParameters): Observable<CollectInputsResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.collectInputs(parameters) as Promise<CollectInputsResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Cancels an outstanding collectInputs command
   */
  cancelCollectInputs(): Observable<{ error?: StripeTerminalError }> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.cancelCollectInputs() as Promise<{ error?: StripeTerminalError }>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Prints the specified content to the connected reader's printer
   */
  print(content: HTMLCanvasElement): Observable<PrintResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    return from(this.terminal.print(content) as Promise<PrintResponse>).pipe(
      catchError((error: any) => {
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Checks if the terminal is initialized
   */
  isTerminalInitialized(): boolean {
    return this.isInitialized && this.terminal !== null;
  }

  /**
   * Gets the terminal instance (for advanced usage)
   */
  getTerminal(): any {
    return this.terminal;
  }

  /**
   * Creates a PaymentIntent via ws-payment service
   */
  createPaymentIntent(
    wineryId: string,
    amountCents: number,
    currency: string,
    email?: string,
    entityType?: string,
    entityId?: string,
    customerName?: string
  ): Observable<{ clientSecret: string; paymentIntentId?: string }> {
    const payload: any = {
      wineryId,
      amount: amountCents,
      currency: currency.toLowerCase()
    };
    
    if (email) {
      payload.email = email;
    }
    
    if (entityType && entityId) {
      payload.entityType = entityType;
      payload.entityId = entityId;
    }
    
    if (customerName) {
      payload.customerName = customerName;
    }
    
    return resource('crm://wspayments/stripe-terminal/payment-intent')
      .post<{ clientSecret: string; paymentIntentId?: string }>(payload);
  }

  /**
   * Creates an internet PaymentIntent via ws-payment service
   * This is used for v2 flow where payment intent is created as internet payment
   * but card is still collected via terminal reader
   */
  createInternetPaymentIntent(
    wineryId: string,
    amountCents: number,
    currency: string,
    email?: string,
    entityType?: string,
    entityId?: string
  ): Observable<{ clientSecret: string; paymentIntentId?: string }> {
    const payload: any = {
      wineryId,
      amount: amountCents,
      currency: currency.toLowerCase(),
      paymentMethodType: 'card' // Explicitly set for internet payment
    };
    
    if (email) {
      payload.email = email;
    }
    
    if (entityType && entityId) {
      payload.entityType = entityType;
      payload.entityId = entityId;
    }
    
    // Use the same endpoint - backend can handle internet vs terminal based on parameters
    return resource('crm://wspayments/stripe-terminal/payment-intent')
      .post<{ clientSecret: string; paymentIntentId?: string }>(payload);
  }

  /**
   * Captures a PaymentIntent via ws-payment service
   */
  capturePaymentIntent(
    wineryId: string,
    paymentIntentId: string,
    email?: string,
    orderId?: string
  ): Observable<{ paymentIntent?: any; error?: { message: string } }> {
    return resource('crm://wspayments/stripe-terminal/payment-intent/capture')
      .post<{ paymentIntent?: any; error?: { message: string } }>({
        wineryId,
        paymentIntentId,
        email: email,
        orderId: orderId
      });
  }

  /**
   * Creates a SetupIntent via ws-payment service for tokenizing payment methods
   * This is used for subscriptions or saving payment methods for future use
   * 
   * IMPORTANT: Sets usage='off_session' to create a reusable payment method
   * that can be used for subscriptions. Without this, Stripe creates a 
   * 'card_present' payment method which cannot be used for subscriptions.
   */
  createSetupIntent(
    wineryId: string,
    contactId?: number,
    email?: string,
    metadata?: { [key: string]: string }
  ): Observable<{ clientSecret: string; setupIntentId?: string }> {
    const payload: any = {
      wineryId,
      usage: 'off_session'  // Required for subscriptions - creates reusable payment method
    };
    
    if (contactId) {
      payload.contactId = contactId;
    }
    
    if (email) {
      payload.email = email;
    }
    
    if (metadata) {
      payload.metadata = metadata;
    }
    
    return resource('crm://wspayments/stripe-terminal/setup-intent')
      .post<{ clientSecret: string; setupIntentId?: string }>(payload);
  }

  /**
   * Collects and confirms a SetupIntent payment method via terminal
   * This tokenizes the card for future use (subscriptions, saved payment methods)
   * This is a high-level method that creates SetupIntent, collects payment method, and confirms it
   */
  async collectAndConfirmSetupIntentPaymentMethod(
    wineryId: string,
    contactId?: number,
    email?: string,
    metadata?: { [key: string]: string }
  ): Promise<{ success: boolean; setupIntent?: any; paymentMethodId?: string; error?: string }> {
    if (!this.terminal) {
      return { success: false, error: 'Terminal not initialized' };
    }

    const reader = this.getReader();
    if (!reader) {
      return { success: false, error: 'No reader connected' };
    }

    try {
      // Step 1: Create SetupIntent via ws-payment service
      console.log('Creating SetupIntent for tokenizing payment method...', { wineryId, contactId, email });
      
      const intentResponse = await this.createSetupIntent(wineryId, contactId, email, metadata).toPromise();
      
      if (!intentResponse || !intentResponse.clientSecret) {
        console.error('Failed to create SetupIntent:', intentResponse);
        return { success: false, error: 'Failed to create setup intent' };
      }

      console.log('SetupIntent created successfully');
      console.log('- ClientSecret:', intentResponse.clientSecret);
      console.log('- SetupIntent ID:', intentResponse.setupIntentId);

      // Step 2: Collect payment method using SetupIntent
      console.log('Collecting payment method for SetupIntent...');
      console.log('- ClientSecret:', intentResponse.clientSecret);
      console.log('- Terminal initialized:', !!this.terminal);
      console.log('- Reader connected:', !!reader);
      console.log('- Reader ID:', reader?.id);
      
      const collectResult = await this.collectSetupIntentPaymentMethod(
        intentResponse.clientSecret,
        'always',
        {
          enable_customer_cancellation: true
        }
      ).toPromise();

      console.log('Collection result received:', {
        hasResult: !!collectResult,
        hasError: !!collectResult?.error,
        hasSetupIntent: !!collectResult?.setupIntent,
        setupIntentStatus: collectResult?.setupIntent?.status,
        errorCode: collectResult?.error?.code,
        errorMessage: collectResult?.error?.message
      });

      if (!collectResult) {
        console.error('No collection result returned from Terminal');
        return { success: false, error: 'Failed to collect payment method - no response from Terminal' };
      }

      if (collectResult.error) {
        console.error('Error collecting payment method:', collectResult.error);
        const errorCode = collectResult.error.code || '';
        const errorMessage = collectResult.error.message || 'Failed to collect payment method';
        
        // Handle cancellation
        if (errorCode === 'canceled' || errorCode === 'cancelled' || errorMessage.toLowerCase().includes('cancel')) {
          return { 
            success: false, 
            error: 'Payment cancelled by user' 
          };
        }
        
        return { 
          success: false, 
          error: errorMessage 
        };
      }

      if (!collectResult.setupIntent) {
        console.error('No SetupIntent in collection result');
        return { success: false, error: 'No setup intent returned from terminal' };
      }

      // Validate SetupIntent status before confirmation
      const setupIntentStatus = collectResult.setupIntent.status;
      console.log('SetupIntent status after collection:', setupIntentStatus);
      console.log('- SetupIntent ID:', collectResult.setupIntent.id);
      console.log('- SetupIntent payment_method:', collectResult.setupIntent.payment_method);
      
      // SetupIntent must be in 'requires_confirmation' or 'requires_action' state to be confirmed
      // Note: If status is 'requires_payment_method', it means the card was not read successfully
      if (setupIntentStatus === 'requires_payment_method') {
        const errorMessage = 'Card was not read successfully. Please try inserting or tapping the card again.';
        console.error(errorMessage);
        return { 
          success: false, 
          error: errorMessage
        };
      }
      
      if (setupIntentStatus !== 'requires_confirmation' && setupIntentStatus !== 'requires_action') {
        const errorMessage = `SetupIntent status is '${setupIntentStatus}', but must be 'requires_confirmation' or 'requires_action' to be confirmed. ` +
          `The payment method may not have been collected successfully from Terminal.`;
        console.error(errorMessage);
        return { 
          success: false, 
          error: errorMessage
        };
      }

      // Step 3: Confirm SetupIntent on backend (server-side)
      // CRITICAL: Do NOT confirm via Terminal SDK - that creates 'card_present' payment method
      // Instead, confirm on backend to get reusable 'card' payment method for subscriptions
      console.log('Confirming SetupIntent on backend (server-side)...');
      console.log('- SetupIntent ID:', collectResult.setupIntent.id);
      console.log('- SetupIntent Status:', setupIntentStatus);
      console.log('- Winery ID:', wineryId);
      
      const confirmResult = await this.confirmSetupIntentOnBackend(
        wineryId,
        collectResult.setupIntent.id
      ).toPromise();

      if (!confirmResult.success || !confirmResult.paymentMethodId) {
        const errorMessage = confirmResult.error || 'Failed to confirm SetupIntent. Payment method could not be created.';
        console.error('Error confirming SetupIntent on backend:', errorMessage);
        return { 
          success: false, 
          error: errorMessage
        };
      }

      // Extract payment method ID from backend-confirmed SetupIntent
      // CRITICAL: Backend confirmation creates a reusable 'card' type payment method,
      // NOT 'card_present'. This payment method can be attached to customers and used for subscriptions.
      // 
      // Note: generated_card payment methods from Terminal (with setup_future_usage) are type "card" 
      // and can be used for subscriptions. This is the generated card that should be passed into 
      // create subscription call. card_present payment methods cannot be attached to customers.
      const paymentMethodId = confirmResult.paymentMethodId;
      
      console.log('SetupIntent confirmed successfully on backend');
      console.log('- Payment Method ID:', paymentMethodId);
      console.log('- SetupIntent Status:', confirmResult.setupIntent?.status);
      console.log('- Payment Method Source: Backend-confirmed SetupIntent (reusable card type - generated_card)');

      return {
        success: true,
        setupIntent: confirmResult.setupIntent,
        paymentMethodId: paymentMethodId
      };

    } catch (error) {
      const err = error as any;
      console.error('Exception during SetupIntent collection:', error);
      return { 
        success: false, 
        error: err?.message || 'Failed to collect and confirm SetupIntent' 
      };
    }
  }

  /**
   * Collects cart confirmation from customer using collectInputs
   * Shows cart on terminal first for 8 seconds, then confirmation screen with "OK" and "Show again" buttons
   * If "Show again" is pressed, redisplay cart for 8 seconds, then return to confirmation
   */
  collectCartConfirmation(cartData: SetReaderDisplayCart, timeoutSeconds: number = 15): Observable<CollectInputsResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    const displayInfo: SetReaderDisplayInfo = {
      type: 'cart',
      cart: cartData
    };

    // Helper function to display cart for 8 seconds
    const displayCartFor8Seconds = async (): Promise<void> => {
      await this.setReaderDisplay(displayInfo).toPromise();
      await new Promise(resolve => setTimeout(resolve, 8000));
    };

    // Helper function to collect confirmation with "OK" and "Show again" buttons
    const collectConfirmation = async (): Promise<CollectInputsResponse> => {
      const collectInputsParameters: CollectInputsParameters = {
        inputs: [
          {
            formType: 'selection',
            title: 'Conferma ordine',
            description: 'Premere OK per confermare o "Mostra di nuovo" per rivedere l\'ordine',
            required: false,
            selectionButtons: [
              {
                style: 'primary',
                text: 'OK'
              },
              {
                style: 'secondary',
                text: 'Mostra di nuovo'
              }
            ],
            skipBehavior: {
              resultType: 'timeout',
              skipAfter: timeoutSeconds
            }
          }
        ]
      };

      return this.terminal.collectInputs(collectInputsParameters) as Promise<CollectInputsResponse>;
    };

    // Main flow: display cart first, then collect confirmation in a loop until OK or timeout
    return from((async (): Promise<CollectInputsResponse> => {
      // First, display cart for 8 seconds
      await displayCartFor8Seconds();

      // Then collect confirmation in a loop
      while (true) {
        const result = await collectConfirmation();
        
        // Check if there's an error (timeout or other)
        if (result?.error) {
          // If timeout, proceed with payment
          if (result.error.code === 'input_collection_timeout' || result.error.message?.includes('timeout')) {
            return {}; // Return empty result on timeout (we'll proceed anyway)
          }
          return result;
        }

        // Check which button was selected - try multiple possible response structures
        let buttonText = '';
        let buttonIndex: number | undefined;
        
        // Log the full result for debugging
        console.log('Full collectInputs result:', JSON.stringify(result, null, 2));
        
        // Check for selection property (newer response format)
        if (result?.[0]?.selection) {
          buttonText = result[0].selection;
          console.log('Selection property detected:', buttonText);
        }
        
        // Try different response structures - check all possible paths
        if (!buttonText && result?.[0]?.selectedButton) {
          if (result[0].selectedButton.text) {
            buttonText = result[0].selectedButton.text;
          }
          if (result[0].selectedButton.index !== undefined) {
            buttonIndex = result[0].selectedButton.index;
          }
        }
        
        if (!buttonText && !buttonIndex && result?.inputs?.[0]?.selectedButton) {
          if (result.inputs[0].selectedButton.text) {
            buttonText = result.inputs[0].selectedButton.text;
          }
          if (result.inputs[0].selectedButton.index !== undefined) {
            buttonIndex = result.inputs[0].selectedButton.index;
          }
        }
        
        if (!buttonText && !buttonIndex && result?.selectedButton) {
          if (result.selectedButton.text) {
            buttonText = result.selectedButton.text;
          }
          if (result.selectedButton.index !== undefined) {
            buttonIndex = result.selectedButton.index;
          }
        }
        
        // Try direct access patterns
        if (!buttonText && result?.[0]?.text) {
          buttonText = result[0].text;
        }
        if (!buttonText && typeof result?.[0] === 'string') {
          buttonText = result[0];
        }
        if (buttonIndex === undefined && typeof result?.[0] === 'number') {
          buttonIndex = result[0];
        }
        if (buttonIndex === undefined && result?.[0]?.index !== undefined) {
          buttonIndex = result[0].index;
        }

        console.log('Detected button text:', buttonText);
        console.log('Detected button index:', buttonIndex);

        // If button index is available, use it (0 = OK, 1 = Mostra di nuovo)
        // Note: Button order in selectionButtons array: [0] = OK, [1] = Mostra di nuovo
        if (buttonIndex !== undefined) {
          console.log(`Button index detected: ${buttonIndex}`);
          if (buttonIndex === 0) {
            // OK button selected (first button) - proceed
            console.log('OK button selected (index 0) - proceeding with payment');
            return result;
          } else if (buttonIndex === 1) {
            // "Mostra di nuovo" button selected (second button) - redisplay cart
            console.log('"Mostra di nuovo" button selected (index 1) - redisplaying cart');
            await displayCartFor8Seconds();
            continue;
          } else {
            // Unknown button index - log and show cart again to be safe
            console.warn(`Unknown button index: ${buttonIndex}. Showing cart again.`);
            await displayCartFor8Seconds();
            continue;
          }
        }

        // If button text is available, check it
        if (buttonText) {
          const normalizedText = buttonText.trim().toLowerCase();
          console.log(`Button text detected: "${buttonText}" (normalized: "${normalizedText}")`);
          
          // If "OK" was selected, proceed
          if (normalizedText === 'ok') {
            console.log('OK button selected (text) - proceeding with payment');
            return result;
          }
          
          // If "Mostra di nuovo" (Show again) was selected, redisplay cart for 8 seconds
          if (normalizedText.includes('mostra') || normalizedText.includes('di nuovo') || normalizedText.includes('show')) {
            console.log('"Mostra di nuovo" button selected (text) - redisplaying cart');
            await displayCartFor8Seconds();
            continue;
          }
          
          // If we have button text but it's not recognized, log and show cart to be safe
          console.warn(`Unrecognized button text: "${buttonText}". Showing cart again to be safe.`);
          await displayCartFor8Seconds();
          continue;
        }

        // If we can't determine which button was pressed, log and proceed (timeout case)
        // This handles the case where timeout occurred or response structure is unexpected
        console.warn('Could not determine which button was selected. Result:', result);
        console.log('Proceeding with payment (assuming timeout or OK)');
        return result;
      }
    })()).pipe(
      catchError((error: any) => {
        // If timeout occurs, that's expected - return success
        if (error?.code === 'input_collection_timeout' || error?.message?.includes('timeout')) {
          return of({}); // Return empty result on timeout (we'll proceed anyway)
        }
        return of({ error: { code: error.code || 'unknown', message: error.message || 'Unknown error' } });
      })
    );
  }

  /**
   * Collects mandatory customer inputs (email and GDPR consents) from Stripe Terminal reader
   * This is the first step - email and GDPR are required to proceed
   */
  collectGuestCustomerInputsMandatory(): Observable<CollectInputsResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    // First collection: Email and GDPR (mandatory)
    const collectInputsParameters: CollectInputsParameters = {
      inputs: [
        {
          formType: 'email',
          title: 'Email',
          description: 'Inserisci la tua email per ricevere la ricevuta',
          required: true
        },
        {
          formType: 'selection',
          title: 'Consensi GDPR',
          description: 'Seleziona i consensi richiesti (marketing opzionale)',
          required: true,
          selectionButtons: [
            {
              style: 'primary',
              text: 'Continua'
            }
          ],
          toggles: [
            {
              title: 'Accetto i termini di privacy',
              defaultValue: 'disabled'
            },
            {
              title: 'Accetto di ricevere comunicazioni di marketing',
              defaultValue: 'disabled'
            },
            {
              title: 'Accetto il trattamento dei dati per profilazione',
              defaultValue: 'disabled'
            }
          ]
        }
      ]
    };

    return this.collectInputs(collectInputsParameters);
  }

  /**
   * Collects guest customer inputs for v2 flow: email, name, surname
   * This is the first collection step for v2
   */
  collectGuestCustomerInputsV2First(): Observable<CollectInputsResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    const collectInputsParameters: CollectInputsParameters = {
      inputs: [
        {
          formType: 'email',
          title: 'Email',
          description: 'Inserisci la tua email per ricevere la ricevuta',
          required: true
        },
        {
          formType: 'text',
          title: 'Nome',
          description: 'Inserisci il tuo nome',
          required: true
        },
        {
          formType: 'text',
          title: 'Cognome',
          description: 'Inserisci il tuo cognome',
          required: true
        }
      ]
    };

    return this.collectInputs(collectInputsParameters);
  }

  /**
   * Collects privacy consents (GDPR) for v2 flow
   * This is the second collection step for v2
   */
  collectGuestCustomerInputsV2Privacy(): Observable<CollectInputsResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    const collectInputsParameters: CollectInputsParameters = {
      inputs: [
        {
          formType: 'selection',
          title: 'Consensi GDPR',
          description: 'Seleziona i consensi richiesti (marketing opzionale)',
          required: true,
          selectionButtons: [
            {
              style: 'primary',
              text: 'Continua'
            }
          ],
          toggles: [
            {
              title: 'Accetto i termini di privacy',
              defaultValue: 'disabled'
            },
            {
              title: 'Accetto di ricevere comunicazioni di marketing',
              defaultValue: 'disabled'
            },
            {
              title: 'Accetto il trattamento dei dati per profilazione',
              defaultValue: 'disabled'
            }
          ]
        }
      ]
    };

    return this.collectInputs(collectInputsParameters);
  }

  /**
   * Collects optional customer inputs (name, surname, phone, country, birth date) from Stripe Terminal reader
   * This is the second step - all fields are optional
   */
  collectGuestCustomerInputsOptional(): Observable<CollectInputsResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    // Second collection: Name, Surname, Phone, Country, Birth Date (all optional)
    const collectInputsParameters: CollectInputsParameters = {
      inputs: [
        {
          formType: 'text',
          title: 'Nome',
          description: 'Inserisci il tuo nome (opzionale)',
          required: false
        },
        {
          formType: 'text',
          title: 'Cognome',
          description: 'Inserisci il tuo cognome (opzionale)',
          required: false
        },
        {
          formType: 'phone',
          title: 'Telefono',
          description: 'Inserisci il tuo numero di telefono (opzionale)',
          required: false
        },
        {
          formType: 'text',
          title: 'Paese',
          description: 'Inserisci il codice paese (es: IT, US, FR) (opzionale)',
          required: false
        },
        {
          formType: 'text',
          title: 'Data di nascita',
          description: 'Inserisci la data di nascita (formato: GG/MM/AAAA) (opzionale)',
          required: false
        }
      ]
    };

    return this.collectInputs(collectInputsParameters);
  }

  /**
   * Collects customer inputs from Stripe Terminal reader using the SDK directly
   * This is used to collect guest customer information (name, surname, email, phone) and GDPR consents on the terminal
   * Uses the SDK's collectInputs method which displays forms on the terminal screen
   * All fields are optional except email, which is required
   * @deprecated Use collectGuestCustomerInputsMandatory and collectGuestCustomerInputsOptional instead
   */
  collectGuestCustomerInputs(): Observable<CollectInputsResponse> {
    if (!this.terminal) {
      return of({ error: { code: 'not_initialized', message: 'Terminal not initialized' } });
    }

    // Format inputs according to Stripe Terminal SDK format
    // This will display forms on the terminal to collect: Nome, Cognome, Email, Telefono, Paese
    // All fields are optional except email (which is required)
    const collectInputsParameters: CollectInputsParameters = {
      inputs: [
        {
          formType: 'text',
          title: 'Nome',
          description: 'Inserisci il tuo nome (opzionale)',
          required: false
        },
        {
          formType: 'text',
          title: 'Cognome',
          description: 'Inserisci il tuo cognome (opzionale)',
          required: false
        },
        {
          formType: 'email',
          title: 'Email',
          description: 'Inserisci la tua email per ricevere la ricevuta',
          required: true
        },
        {
          formType: 'phone',
          title: 'Telefono',
          description: 'Inserisci il tuo numero di telefono (opzionale)',
          required: false
        },
        {
          formType: 'text',
          title: 'Paese',
          description: 'Inserisci il codice paese (es: IT, US, FR) (opzionale)',
          required: false
        }
        // GDPR consents removed - Stripe Terminal SDK allows max 5 forms
        , {
           formType: 'selection',
           title: 'Consensi GDPR',
           description: 'Seleziona i consensi richiesti (marketing opzionale)',
           required: true,
           selectionButtons: [
             {
               style: 'primary',
               text: 'Continua'
             }
           ],
           toggles: [
             {
               title: 'Accetto i termini di privacy',
               defaultValue: 'disabled'
             },
             {
               title: 'Accetto di ricevere comunicazioni di marketing',
               defaultValue: 'disabled'
             },
             {
               title: 'Accetto il trattamento dei dati per profilazione',
               defaultValue: 'disabled'
             }
           ]
         }
      ]
    };

    return this.collectInputs(collectInputsParameters);
  }

  /**
   * Displays cart for review and collects confirmation from customer
   * This should be called while userReviewOrder step is active
   */
  async displayCartForReview(
    cartData: SetReaderDisplayCart,
    timeoutSeconds: number = 15
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isTerminalInitialized()) {
      return { success: false, error: 'Terminal not initialized' };
    }

    // Check if reader is connected
    if (this.getConnectionStatus() !== 'connected') {
      return { success: false, error: 'Terminal reader is not connected' };
    }

    try {
      console.log('=== DISPLAYING CART FOR REVIEW ===');
      const cartConfirmationResult = await this.collectCartConfirmation(cartData, timeoutSeconds).toPromise();
      
      if (cartConfirmationResult?.error) {
        // If error is timeout, that's OK - we proceed with payment
        if (cartConfirmationResult.error.code !== 'input_collection_timeout') {
          console.warn('Cart confirmation error (non-timeout):', cartConfirmationResult.error);
          return { success: false, error: cartConfirmationResult.error.message || 'Cart confirmation failed' };
        } else {
          console.log('Cart confirmation timed out after 15s - proceeding with payment');
          return { success: true };
        }
      } else {
        console.log('Cart confirmed by customer - proceeding with payment');
        return { success: true };
      }
    } catch (error) {
      const err = error as any;
      console.warn('Error during cart confirmation:', error);
      return { success: false, error: err.message || 'Cart confirmation error' };
    }
  }

  /**
   * Creates payment intent and collects payment from customer
   * This should be called after cart review is completed (userAboutToPay step)
   */
  async createIntentAndCollectPayment(
    wineryId: string,
    amountCents: number,
    currency: string = 'eur',
    options?: {
      skipTipping?: boolean;
      enableCustomerCancellation?: boolean;
      email?: string;
      entityType?: string;
      entityId?: string;
      customerName?: string;
      cartData?: SetReaderDisplayCart;
    }
  ): Promise<{ success: boolean; paymentIntent?: any; error?: string }> {
    if (!this.isTerminalInitialized()) {
      return { success: false, error: 'Terminal not initialized' };
    }

    // Check if reader is connected
    if (this.getConnectionStatus() !== 'connected') {
      return { success: false, error: 'Terminal reader is not connected' };
    }

    try {
      console.log('Creating PaymentIntent for Terminal payment...', { wineryId, amountCents, currency });
      
      // Step 1: Create PaymentIntent via ws-payment service
      const intentResponse = await this.createPaymentIntent(
        wineryId, 
        amountCents, 
        currency, 
        options?.email,
        options?.entityType,
        options?.entityId,
        options?.customerName
      ).toPromise();
      
      if (!intentResponse || !intentResponse.clientSecret) {
        console.error('Failed to create PaymentIntent:', intentResponse);
        return { success: false, error: 'Failed to create payment intent' };
      }

      console.log('PaymentIntent created successfully');
      console.log('- ClientSecret:', intentResponse.clientSecret);
      console.log('- PaymentIntent ID:', intentResponse.paymentIntentId);
      console.log('- Amount:', amountCents, currency);
      
      // Verify terminal is ready
      if (!this.terminal) {
        return { success: false, error: 'Terminal instance not available' };
      }
      
      const reader = this.getReader();
      if (!reader) {
        return { success: false, error: 'No reader connected' };
      }
      
      console.log('Terminal ready:');
      console.log('- Reader ID:', reader.id);
      console.log('- Reader type:', reader.device_type);
      console.log('- Reader status:', reader.status);
      
      // Step 2: Collect payment method (user has confirmed the order)
      console.log('=== COLLECTING PAYMENT ===');
      console.log('ClientSecret to send:', intentResponse.clientSecret);
      console.log('PaymentIntent ID:', intentResponse.paymentIntentId);
      console.log('Amount:', amountCents, currency);
      console.log('Terminal instance:', this.terminal ? 'available' : 'MISSING');
      console.log('Reader:', reader ? `${reader.id} (${reader.device_type})` : 'MISSING');
      
      let collectResult: CollectPaymentMethodResponse;
      try {
        // Collect payment method
        console.log('Calling terminal.collectPaymentMethod()...');
        collectResult = await this.collectPaymentMethod(intentResponse.clientSecret, {
          config_override: {
            skip_tipping: options?.skipTipping || false,
            enable_customer_cancellation: options?.enableCustomerCancellation || false
          }
        }).toPromise();
        
        console.log('=== COLLECT PAYMENT METHOD RESULT ===');
        console.log('Result received:', collectResult);
        console.log('Has error:', !!collectResult?.error);
        console.log('Has paymentIntent:', !!collectResult?.paymentIntent);
        
        if (collectResult?.paymentIntent) {
          console.log('PaymentIntent from terminal:', {
            id: collectResult.paymentIntent.id,
            status: collectResult.paymentIntent.status,
            amount: collectResult.paymentIntent.amount,
            currency: collectResult.paymentIntent.currency
          });
          console.log('✅ Terminal successfully collected payment method from customer');
        }
        
        if (collectResult?.error) {
          console.error('=== TERMINAL ERROR ===');
          console.error('Error code:', collectResult.error.code);
          console.error('Error message:', collectResult.error.message);
          console.error('Full error:', collectResult.error);
        }

        if (collectResult?.error) {
          console.error('Error collecting payment method:', collectResult.error);
          // Clear display on error
          await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on error:', err));
          return { success: false, error: collectResult.error.message || 'Payment collection failed' };
        }

        if (!collectResult?.paymentIntent) {
          console.error('No paymentIntent in collectResult:', collectResult);
          // Clear display on error
          await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on error:', err));
          return { success: false, error: 'Payment collection failed - no payment intent returned' };
        }
        
        console.log('Payment method collected successfully. PaymentIntent:', collectResult.paymentIntent.id);
      } catch (error) {
        const err = error as any;
        console.error('Exception during collectPaymentMethod:', error);
        
        // Clear display on exception
        await this.clearReaderDisplay().toPromise().catch(displayError => console.warn('Could not clear display on exception:', displayError));
        
        return { success: false, error: err.message || 'Failed to collect payment method from terminal' };
      }

      // Check PaymentIntent status
      const paymentIntentStatus = collectResult.paymentIntent?.status;
      console.log('PaymentIntent status after collection:', paymentIntentStatus);

      // If PaymentIntent is already succeeded, it means it was auto-confirmed (wrong configuration)
      if (paymentIntentStatus === 'succeeded') {
        console.warn('PaymentIntent was already succeeded - this indicates auto-confirmation. The backend should use confirmation_method: manual');
        // Still return success but log the issue
        return { success: true, paymentIntent: collectResult.paymentIntent };
      }

      // PaymentIntent should be in 'requires_confirmation' or 'requires_capture' state after collection
      if (paymentIntentStatus !== 'requires_confirmation' && paymentIntentStatus !== 'requires_capture') {
        console.warn('Unexpected PaymentIntent status after collection:', paymentIntentStatus);
      }

      // Step 3: Process payment using the existing processPayment method
      // This should send the payment to Stripe for processing
      console.log('Processing payment...');
      const processResult = await this.processPayment(collectResult.paymentIntent).toPromise();

      console.log('processPayment result:', processResult);

      if (processResult?.error) {
        console.error('Error processing payment:', processResult.error);
        
        // Clear display on error
        await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on processing error:', err));
        
        return { success: false, error: processResult.error.message || 'Payment processing failed' };
      }

      if (processResult?.paymentIntent) {
        const finalStatus = processResult.paymentIntent?.status;
        console.log('Payment processed successfully. Final status:', finalStatus);
        
        // If payment requires capture, automatically capture it
        if (finalStatus === 'requires_capture') {
          console.log('Payment requires capture - capturing automatically...');
          const captureResult = await this.capturePaymentIntent(
            wineryId, 
            processResult.paymentIntent.id,
            options?.email
          ).toPromise();
          
          if (captureResult?.error) {
            console.error('Error capturing payment:', captureResult.error);
            
            // Clear display on capture error
            await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on capture error:', err));
            
            return { success: false, error: captureResult.error.message || 'Payment capture failed' };
          }
          
          if (captureResult?.paymentIntent?.status === 'succeeded') {
            console.log('✅ Payment captured successfully. Status: succeeded');
            
            // Show cart for 5 seconds after successful payment (last order resume), then auto-close
            // Do not include cancellation line (fake line)
            if (options?.cartData) {
              console.log('Displaying cart for 5 seconds after successful payment (last order resume)...');
              try {
                // Use cart data directly without enhancement (no cancellation line)
                await this.setReaderDisplay({
                  type: 'cart',
                  cart: options.cartData
                }).toPromise();
                
                // Wait 5 seconds then auto-close
                setTimeout(async () => {
                  await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display after timeout:', err));
                }, 5000);
              } catch (displayError) {
                console.warn('Could not display cart after capture:', displayError);
              }
            }
            
            return { success: true, paymentIntent: captureResult.paymentIntent };
          } else {
            console.warn('Payment capture completed but status is:', captureResult?.paymentIntent?.status);
            // Still return success if capture was attempted
            return { success: true, paymentIntent: captureResult?.paymentIntent || processResult.paymentIntent };
          }
        }
        
        if (finalStatus === 'succeeded') {
          console.log('✅ Payment succeeded without capture needed');
          
          // Show cart for 5 seconds after successful payment (last order resume), then auto-close
          // Do not include cancellation line (fake line)
          if (options?.cartData) {
            console.log('Displaying cart for 5 seconds after successful payment (last order resume)...');
            try {
              // Use cart data directly without enhancement (no cancellation line)
              await this.setReaderDisplay({
                type: 'cart',
                cart: options.cartData
              }).toPromise();
              
              // Wait 5 seconds then auto-close
              setTimeout(async () => {
                await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display after timeout:', err));
              }, 5000);
            } catch (displayError) {
              console.warn('Could not display cart after payment:', displayError);
            }
          }
          
          return { success: true, paymentIntent: processResult.paymentIntent };
        } else {
          // Clear display on unexpected status
          await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on unexpected status:', err));
          return { success: false, error: `Payment status is ${finalStatus}, expected succeeded or requires_capture` };
        }
      }

      // Clear display if no payment intent returned
      await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on processing failure:', err));

      return { success: false, error: 'Payment processing failed - no payment intent returned' };
    } catch (error) {
      const err = error as any;
      console.error('Error processing payment with Stripe Terminal:', error);
      
      // Clear display on exception
      await this.clearReaderDisplay().toPromise().catch(displayError => console.warn('Could not clear display on exception:', displayError));
      
      return { success: false, error: err.message || 'Payment processing error' };
    }
  }

  /**
   * Processes payment through Stripe Terminal
   * This method handles the complete flow: create PaymentIntent -> collect payment -> process payment
   * @deprecated Use displayCartForReview and createIntentAndCollectPayment instead for better step control
   */
  async processPaymentWithTerminal(
    wineryId: string,
    amountCents: number,
    currency: string = 'eur',
    options?: {
      skipTipping?: boolean;
      enableCustomerCancellation?: boolean;
      email?: string;
      entityType?: string;
      entityId?: string;
      customerName?: string;
      cartData?: SetReaderDisplayCart;
    }
  ): Promise<{ success: boolean; paymentIntent?: any; error?: string }> {
    if (!this.isTerminalInitialized()) {
      return { success: false, error: 'Terminal not initialized' };
    }

    // Check if reader is connected
    if (this.getConnectionStatus() !== 'connected') {
      return { success: false, error: 'Terminal reader is not connected' };
    }

    try {
      console.log('Creating PaymentIntent for Terminal payment...', { wineryId, amountCents, currency });
      
      // Step 1: Create PaymentIntent via ws-payment service
      const intentResponse = await this.createPaymentIntent(
        wineryId, 
        amountCents, 
        currency, 
        options?.email,
        options?.entityType,
        options?.entityId,
        options?.customerName
      ).toPromise();
      
      if (!intentResponse || !intentResponse.clientSecret) {
        console.error('Failed to create PaymentIntent:', intentResponse);
        return { success: false, error: 'Failed to create payment intent' };
      }

      console.log('PaymentIntent created successfully');
      console.log('- ClientSecret:', intentResponse.clientSecret);
      console.log('- PaymentIntent ID:', intentResponse.paymentIntentId);
      console.log('- Amount:', amountCents, currency);
      
      // Verify terminal is ready
      if (!this.terminal) {
        return { success: false, error: 'Terminal instance not available' };
      }
      
      const reader = this.getReader();
      if (!reader) {
        return { success: false, error: 'No reader connected' };
      }
      
      console.log('Terminal ready:');
      console.log('- Reader ID:', reader.id);
      console.log('- Reader type:', reader.device_type);
      console.log('- Reader status:', reader.status);
      
      // Step 2: Show cart confirmation screen first (with 15s timeout)
      if (options?.cartData) {
        console.log('=== SHOWING CART CONFIRMATION ===');
        try {
          const cartConfirmationResult = await this.collectCartConfirmation(options.cartData, 15).toPromise();
          
          if (cartConfirmationResult?.error) {
            // If error is timeout, that's OK - we proceed with payment
            if (cartConfirmationResult.error.code !== 'input_collection_timeout') {
              console.warn('Cart confirmation error (non-timeout):', cartConfirmationResult.error);
              // Still proceed with payment even if confirmation had an error
            } else {
              console.log('Cart confirmation timed out after 15s - proceeding with payment');
            }
          } else {
            console.log('Cart confirmed by customer - proceeding with payment');
          }
        } catch (error) {
          console.warn('Error during cart confirmation, proceeding with payment:', error);
          // Continue with payment even if confirmation failed
        }
      }
      
      // Step 3: Collect payment method (user has confirmed the order or timeout occurred)
      console.log('=== COLLECTING PAYMENT ===');
      console.log('ClientSecret to send:', intentResponse.clientSecret);
      console.log('PaymentIntent ID:', intentResponse.paymentIntentId);
      console.log('Amount:', amountCents, currency);
      console.log('Terminal instance:', this.terminal ? 'available' : 'MISSING');
      console.log('Reader:', reader ? `${reader.id} (${reader.device_type})` : 'MISSING');
      
      let collectResult: CollectPaymentMethodResponse;
      try {
        // Collect payment method
        console.log('Calling terminal.collectPaymentMethod()...');
        collectResult = await this.collectPaymentMethod(intentResponse.clientSecret, {
          config_override: {
            skip_tipping: options?.skipTipping || false,
            enable_customer_cancellation: options?.enableCustomerCancellation || false
          }
        }).toPromise();
        
        console.log('=== COLLECT PAYMENT METHOD RESULT ===');
        console.log('Result received:', collectResult);
        console.log('Has error:', !!collectResult?.error);
        console.log('Has paymentIntent:', !!collectResult?.paymentIntent);
        
        if (collectResult?.paymentIntent) {
          console.log('PaymentIntent from terminal:', {
            id: collectResult.paymentIntent.id,
            status: collectResult.paymentIntent.status,
            amount: collectResult.paymentIntent.amount,
            currency: collectResult.paymentIntent.currency
          });
          console.log('✅ Terminal successfully collected payment method from customer');
        }
        
        if (collectResult?.error) {
          console.error('=== TERMINAL ERROR ===');
          console.error('Error code:', collectResult.error.code);
          console.error('Error message:', collectResult.error.message);
          console.error('Full error:', collectResult.error);
        }

        if (collectResult?.error) {
          console.error('Error collecting payment method:', collectResult.error);
          // Clear display on error
          await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on error:', err));
          return { success: false, error: collectResult.error.message || 'Payment collection failed' };
        }

        if (!collectResult?.paymentIntent) {
          console.error('No paymentIntent in collectResult:', collectResult);
          // Clear display on error
          await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on error:', err));
          return { success: false, error: 'Payment collection failed - no payment intent returned' };
        }
        
        console.log('Payment method collected successfully. PaymentIntent:', collectResult.paymentIntent.id);
      } catch (error) {
        const err = error as any;
        console.error('Exception during collectPaymentMethod:', error);
        
        // Clear display on exception
        await this.clearReaderDisplay().toPromise().catch(displayError => console.warn('Could not clear display on exception:', displayError));
        
        return { success: false, error: err.message || 'Failed to collect payment method from terminal' };
      }

      // Check PaymentIntent status
      const paymentIntentStatus = collectResult.paymentIntent?.status;
      console.log('PaymentIntent status after collection:', paymentIntentStatus);

      // If PaymentIntent is already succeeded, it means it was auto-confirmed (wrong configuration)
      if (paymentIntentStatus === 'succeeded') {
        console.warn('PaymentIntent was already succeeded - this indicates auto-confirmation. The backend should use confirmation_method: manual');
        // Still return success but log the issue
        return { success: true, paymentIntent: collectResult.paymentIntent };
      }

      // PaymentIntent should be in 'requires_confirmation' or 'requires_capture' state after collection
      if (paymentIntentStatus !== 'requires_confirmation' && paymentIntentStatus !== 'requires_capture') {
        console.warn('Unexpected PaymentIntent status after collection:', paymentIntentStatus);
      }

      // Step 4: Process payment using the existing processPayment method
      // This should send the payment to Stripe for processing
      console.log('Processing payment...');
      const processResult = await this.processPayment(collectResult.paymentIntent).toPromise();

      console.log('processPayment result:', processResult);

      if (processResult?.error) {
        console.error('Error processing payment:', processResult.error);
        
        // Clear display on error
        await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on processing error:', err));
        
        return { success: false, error: processResult.error.message || 'Payment processing failed' };
      }

      if (processResult?.paymentIntent) {
        const finalStatus = processResult.paymentIntent?.status;
        console.log('Payment processed successfully. Final status:', finalStatus);
        
        // If payment requires capture, automatically capture it
        if (finalStatus === 'requires_capture') {
          console.log('Payment requires capture - capturing automatically...');
          const captureResult = await this.capturePaymentIntent(
            wineryId, 
            processResult.paymentIntent.id,
            options?.email
          ).toPromise();
          
          if (captureResult?.error) {
            console.error('Error capturing payment:', captureResult.error);
            
            // Clear display on capture error
            await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on capture error:', err));
            
            return { success: false, error: captureResult.error.message || 'Payment capture failed' };
          }
          
          if (captureResult?.paymentIntent?.status === 'succeeded') {
            console.log('✅ Payment captured successfully. Status: succeeded');
            
            // Show cart for 5 seconds after successful payment (last order resume), then auto-close
            // Do not include cancellation line (fake line)
            if (options?.cartData) {
              console.log('Displaying cart for 5 seconds after successful payment (last order resume)...');
              try {
                // Use cart data directly without enhancement (no cancellation line)
                await this.setReaderDisplay({
                  type: 'cart',
                  cart: options.cartData
                }).toPromise();
                
                // Wait 5 seconds then auto-close
                setTimeout(async () => {
                  await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display after timeout:', err));
                }, 5000);
              } catch (displayError) {
                console.warn('Could not display cart after capture:', displayError);
              }
            }
            
            return { success: true, paymentIntent: captureResult.paymentIntent };
          } else {
            console.warn('Payment capture completed but status is:', captureResult?.paymentIntent?.status);
            // Still return success if capture was attempted
            return { success: true, paymentIntent: captureResult?.paymentIntent || processResult.paymentIntent };
          }
        }
        
        if (finalStatus === 'succeeded') {
          console.log('✅ Payment succeeded without capture needed');
          
          // Show cart for 5 seconds after successful payment (last order resume), then auto-close
          // Do not include cancellation line (fake line)
          if (options?.cartData) {
            console.log('Displaying cart for 5 seconds after successful payment (last order resume)...');
            try {
              // Use cart data directly without enhancement (no cancellation line)
              await this.setReaderDisplay({
                type: 'cart',
                cart: options.cartData
              }).toPromise();
              
              // Wait 5 seconds then auto-close
              setTimeout(async () => {
                await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display after timeout:', err));
              }, 5000);
            } catch (displayError) {
              console.warn('Could not display cart after payment:', displayError);
            }
          }
          
          return { success: true, paymentIntent: processResult.paymentIntent };
        } else {
          // Clear display on unexpected status
          await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on unexpected status:', err));
          return { success: false, error: `Payment status is ${finalStatus}, expected succeeded or requires_capture` };
        }
      }

      // Clear display if no payment intent returned
      await this.clearReaderDisplay().toPromise().catch(err => console.warn('Could not clear display on processing failure:', err));

      return { success: false, error: 'Payment processing failed - no payment intent returned' };
    } catch (error) {
      const err = error as any;
      console.error('Error processing payment with Stripe Terminal:', error);
      
      // Clear display on exception
      await this.clearReaderDisplay().toPromise().catch(displayError => console.warn('Could not clear display on exception:', displayError));
      
      return { success: false, error: err.message || 'Payment processing error' };
    }
  }
}

